/**
 * useCanvasZoom Hook
 * Handles zoom controls and wheel zoom
 */

import { useEffect, useCallback, RefObject } from 'react'
import { dia } from '@joint/core'

interface UseCanvasZoomReturn {
  handleZoomIn: () => void
  handleZoomOut: () => void
  handleZoomReset: () => void
  handleFitToScreen: () => void
}

export function useCanvasZoom(
  paper: dia.Paper | null,
  canvasRef: RefObject<HTMLDivElement>,
  onZoomChange?: (zoom: number) => void
): UseCanvasZoomReturn {
  const zoomToPoint = useCallback(
    (nextScale: number, x: number, y: number) => {
      if (!paper) return

      const currentScale = paper.scale().sx
      const currentTranslate = paper.translate()

      // Calculate new translation to keep the point (x, y) fixed
      const newTx = x - (x - currentTranslate.tx) * (nextScale / currentScale)
      const newTy = y - (y - currentTranslate.ty) * (nextScale / currentScale)

      paper.scale(nextScale, nextScale)
      paper.translate(newTx, newTy)

      if (onZoomChange) {
        onZoomChange(nextScale)
      }
    },
    [paper, onZoomChange]
  )

  const handleZoomIn = useCallback(() => {
    if (paper) {
      const currentScale = paper.scale()
      const nextScale = Math.min(currentScale.sx * 1.2, 4) // Max 400%
      const viewportWidth = paper.el.parentElement?.clientWidth || 800
      const viewportHeight = paper.el.parentElement?.clientHeight || 600
      const ox = viewportWidth / 2
      const oy = viewportHeight / 2
      zoomToPoint(nextScale, ox, oy)
    }
  }, [paper, zoomToPoint])

  const handleZoomOut = useCallback(() => {
    if (paper) {
      const currentScale = paper.scale()
      // Min 1% — handleFitToScreen 과 동일한 하한(0.01). 종전 0.02 는 fit-to-screen 의
      // 0.01 보다 높아서, fit 으로 도달한 축소 배율보다도 더 축소가 안 되는 경우가 있었음.
      const nextScale = Math.max(currentScale.sx / 1.2, 0.01)
      const viewportWidth = paper.el.parentElement?.clientWidth || 800
      const viewportHeight = paper.el.parentElement?.clientHeight || 600
      const ox = viewportWidth / 2
      const oy = viewportHeight / 2
      zoomToPoint(nextScale, ox, oy)
    }
  }, [paper, zoomToPoint])

  const handleZoomReset = useCallback(() => {
    if (paper) {
      paper.scale(1, 1)
      if (onZoomChange) {
        onZoomChange(1)
      }
    }
  }, [paper, onZoomChange])

  const handleFitToScreen = useCallback(() => {
    paper?.scaleContentToFit({
      padding: 50,
      maxScale: 4,
      // normalize 보정으로 graph 가 매우 커질 수 있어 (예: 17,000px 폭) 0.1 한계로는
      // fit 불가능. 0.01 까지 허용.
      minScale: 0.01,
    })
  }, [paper])

  // Wheel zoom handler
  useEffect(() => {
    // Coalesce rapid wheel events (trackpad inertia fires 20-60/s) into a single
    // zoom per animation frame. Each raw event would otherwise trigger paper
    // scale/translate + downstream re-renders (StatusOverlay, LOD) synchronously.
    let pendingFactor = 1
    let pendingPivot: { x: number; y: number } | null = null
    let rafId: number | null = null

    const applyPendingZoom = () => {
      rafId = null
      if (!paper || !pendingPivot) return

      const currentScale = paper.scale().sx
      const nextScale = Math.max(0.01, Math.min(currentScale * pendingFactor, 4))
      const pivot = pendingPivot

      pendingFactor = 1
      pendingPivot = null

      zoomToPoint(nextScale, pivot.x, pivot.y)
    }

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()

      if (!paper) return

      const rect = paper.el.parentElement?.getBoundingClientRect()
      if (!rect) return

      pendingFactor *= e.deltaY > 0 ? 0.9 : 1.1
      pendingPivot = { x: e.clientX - rect.left, y: e.clientY - rect.top }

      if (rafId === null) {
        rafId = requestAnimationFrame(applyPendingZoom)
      }
    }

    const canvasEl = canvasRef.current
    if (canvasEl) {
      canvasEl.addEventListener('wheel', handleWheel, { passive: false })
    }

    return () => {
      if (canvasEl) {
        canvasEl.removeEventListener('wheel', handleWheel)
      }
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [paper, canvasRef, zoomToPoint])

  return { handleZoomIn, handleZoomOut, handleZoomReset, handleFitToScreen }
}
